import { add, getDay, setHours, setMinutes, format } from 'date-fns'
import { IBusinessHoursByDays, weekDays } from './businessHours'

const setHoursAndMinutesToDate = (d: Date, timeOfDay: string) => {
  const hours = timeOfDay.split(':')[0]
  const minutes = timeOfDay.split(':')[1]
  return setMinutes(setHours(d, Number(hours)), Number(minutes))
}

export const prependZero = (timeOfDay: string) => {
  return timeOfDay.length === 4 ? `0${timeOfDay}` : timeOfDay
}

export const isValidTime = (time) =>
  /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$/.test(time)

/*
 * Returns date of the first useful business day date that equals to the targetNthDay.
 * Example: If there is just one business day in a week (for example Monday) and we need 4th business day, it will actually
 * equal to the 4th week's Monday.
 */
export const findBusinessDate = (
  userJoinDate: Date,
  targetNthDay: number,
  businessDays: IBusinessHoursByDays
): Date => {
  if (targetNthDay < 0) {
    return userJoinDate
  }
  const businessDaysExists =
    businessDays &&
    Object.values(businessDays).filter(
      (day) => isValidTime(day[0]) && isValidTime(day[1])
    ).length > 0

  if (!businessDaysExists) {
    // If organisation does not have even one business day set, we skip this whole logic and return that date
    return userJoinDate
  }

  let currentDate = userJoinDate
  let finalTargetSendDate: Date | null = null

  let currentBusinessDay = 0

  while (!finalTargetSendDate) {
    const businessHoursInThatDay = businessDays[weekDays[getDay(currentDate)]]
    if (
      businessHoursInThatDay &&
      businessHoursInThatDay[0] &&
      businessHoursInThatDay[1]
    ) {
      if (currentBusinessDay === targetNthDay) {
        // Now the currentBusinessDay is equal to the targetNthDay, so it's the day we are searching for
        finalTargetSendDate = currentDate
      } else {
        // This date equals to the business day, however not the one we are searching for, we have to move on
        currentBusinessDay++
        currentDate = add(currentDate, { days: 1 })
      }
    } else {
      // This is no-business day, nothing to do, we have to move on to the next day and test that one.
      currentDate = add(currentDate, { days: 1 })
    }
  }

  return findTimeInBusinessDay(finalTargetSendDate, businessDays)
}

/**
 * @returns always a future date inside business hours
 */
export const findTimeInBusinessDay = (
  sendDate: Date,
  businessDays: IBusinessHoursByDays
): Date => {
  const businessDay = businessDays[weekDays[getDay(sendDate)]]
  const timeOfDay = format(sendDate, 'HH:mm')

  const openFrom = prependZero(businessDay[0])
  const openTo = prependZero(businessDay[1])

  if (timeOfDay > openTo || openFrom > timeOfDay) {
    if (timeOfDay < openFrom) {
      return setHoursAndMinutesToDate(sendDate, openFrom)
    } else if (timeOfDay > openTo) {
      return setHoursAndMinutesToDate(sendDate, openTo)
    }
    return sendDate
  } else {
    return sendDate
  }
}

export const buildBusinessHours = (
  businessHours?: Partial<IBusinessHoursByDays>
): IBusinessHoursByDays => {
  return {
    monday: [null, null],
    tuesday: [null, null],
    wednesday: [null, null],
    thursday: [null, null],
    friday: [null, null],
    saturday: [null, null],
    sunday: [null, null],
    ...businessHours
  }
}
